import { useEffect, useMemo, useState } from "react";
import projectStore from "../stores/ProjectStore";
import { useAuth } from "../context/AuthContext";
import { filterRoutesByServices, HARDCODED_SERVICE_PATHS } from "../utils/serviceRouteMap";
import {
  CustomCatalog,
  DataDonation,
  DataDiscovery,
  DataStewardship,
  DataStagingTicket,
  ServicePage,
} from "./lazyPages";

const servicesRoutes = [
  {
    path: "/data-catalog",
    component: <CustomCatalog key="data" catalogType="data" />,
  },
  {
    path: "/software-catalog",
    component: <CustomCatalog key="software" catalogType="software" />,
  },
  {
    path: "/model-catalog",
    component: <CustomCatalog key="model" catalogType="model" />,
  },
  {
    path: "/donate-dataset",
    component: <DataDonation />,
  },
  {
    path: "/discover-data",
    component: <DataDiscovery />,
  },
  {
    path: "/data-stewardship",
    component: <DataStewardship />,
  },
  {
    path: "/staging-ticket",
    component: <DataStagingTicket />,
  },
];

// Route access state + loading gate for AppRoutes (must be called from an observer component).
export function useRouteAccess() {
  const [isLoading, setIsLoading] = useState(true);

  const {
    allowedServices,
    assignedProjects,
    assignedProjectsLoaded,
    allServices,
    allServicesLoaded,
  } = projectStore;
  const { user } = useAuth();

  const isAdmin = user?.role === "admin" || user?.role === "sysAdmin";
  const canAccessAdminArea = ["sysAdmin", "admin", "staff"].includes(user?.role);
  const canManageTickets = ["sysAdmin", "admin"].includes(user?.role);
  const canManageLegalDocuments = user?.role === "sysAdmin";
  const canMoveMembers = ["sysAdmin", "staff"].includes(user?.role);
  const shouldLoadAssignedProjects = Boolean(
    user && !["admin", "sysAdmin", "official"].includes(user?.role || "user"),
  );
  const canAccessMyProjectsRoute = shouldLoadAssignedProjects && assignedProjects.length > 0;
  const canAccessMyTicketsRoute = Boolean(
    user && shouldLoadAssignedProjects && user.company_id && assignedProjects.length > 0,
  );

  useEffect(() => {
    projectStore.loadAssignedProjects(user);
  }, [user]);

  useEffect(() => {
    if (isAdmin) {
      projectStore.loadAllServices();
    }
  }, [isAdmin]);

  const projectServicesRoutes = useMemo(() => {
    if (user?.role === "admin" || user?.role === "sysAdmin") {
      return servicesRoutes;
    }
    return filterRoutesByServices(servicesRoutes, allowedServices);
  }, [allowedServices, user?.role]);

  // HPC Flows is its own service: registered in the admin panel and assigned to projects.
  const canUseHpcFlows =
    isAdmin || filterRoutesByServices([{ path: "/hpc-flows" }], allowedServices).length > 0;

  const customServiceRoutes = useMemo(() => {
    const services = isAdmin ? allServices : allowedServices;
    if (!Array.isArray(services) || services.length === 0) return [];
    return services
      .filter((s) => s.path && !HARDCODED_SERVICE_PATHS.has(s.path) && !s.path.startsWith("http"))
      .map((s) => ({
        path: s.path,
        component: <ServicePage />,
      }));
  }, [allowedServices, allServices, isAdmin]);

  useEffect(() => {
    // wait for projectServicesRoutes to be computed before rendering routes
    let timeout;

    if (shouldLoadAssignedProjects && !assignedProjectsLoaded) {
      setIsLoading(true);
      return;
    }

    if (isAdmin && !allServicesLoaded) {
      setIsLoading(true);
      return;
    }

    if (projectServicesRoutes.length === 0 && customServiceRoutes.length === 0) {
      timeout = setTimeout(() => {
        setIsLoading(false);
      }, 300);
    } else {
      setIsLoading(false);
    }
    return () => {
      if (timeout) return clearTimeout(timeout);
      return;
    };
  }, [
    assignedProjectsLoaded,
    allServicesLoaded,
    projectServicesRoutes,
    customServiceRoutes,
    shouldLoadAssignedProjects,
    isAdmin,
  ]);

  return {
    isLoading,
    user,
    canAccessAdminArea,
    canManageTickets,
    canManageLegalDocuments,
    canMoveMembers,
    canAccessMyProjectsRoute,
    canAccessMyTicketsRoute,
    projectServicesRoutes,
    canUseHpcFlows,
    customServiceRoutes,
  };
}
